import axios from "axios";
import authHeader from "../../helpers/header";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { TagsInput } from "react-tag-input-component";

import { setMessage, setSuccessMessage } from "../../redux/actions/message";
import AuthLayout from "../../components/AuthLayout/AuthLayout";
import CollectionAPI from "../../api/collectionAPI";

import { API_URL, MEDIA_URL } from "../../configs";

function ResourcesCreateCollection(props) {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm();

  const { message, messageType } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const search = useLocation().search;
  const searchParams = new URLSearchParams(search);
  const [data, setData] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleCollectionTags = (tags) => {
    setSelectedTags(tags);
    setValue("collection_tags", tags.join(","));
  };

  const handleRemoveCollectionTag = (tag) => {
    const updatedTags = selectedTags.filter((t) => t !== tag);
    setSelectedTags(updatedTags);
    setValue("collection_tags", updatedTags.join(","));
  };

  const generateCollectionTags = (event) => {
    const name = event.target.value;
    if (name && name.length > 2) {
      // Split collection name into individual words
      // Remove special characters, split by spaces/hyphens/underscores, and filter out empty strings
      const words = name
        .replace(/[^\w\s-]/g, '') // Remove special characters except spaces, hyphens, underscores
        .split(/[\s\-_]+/) // Split by space, hyphen, or underscore
        .filter(word => word.length > 2) // Only keep words longer than 2 characters
        .map(word => word.toLowerCase()); // Convert to lowercase
      
      // Remove duplicates
      const uniqueWords = [...new Set(words)];
      
      // Automatically add all generated tags to the tags field
      setSelectedTags(uniqueWords);
      setValue("collection_tags", uniqueWords.join(","));
      setSuggestedTags(uniqueWords);
    } else {
      setSelectedTags([]);
      setValue("collection_tags", "");
      setSuggestedTags([]);
    }
  };

  const addSuggestedTag = (tag) => {
    if (!selectedTags.includes(tag)) {
      const newTags = [...selectedTags, tag];
      setSelectedTags(newTags);
      setValue("collection_tags", newTags.join(","));
    }
  };

  // Create collection
  const createCollection = async (formData) => {
    setIsLoading(true);
    const collectionData = {
      collection_name: formData.collection_name,
      collection_tags: selectedTags.join(", "),
      resource_ids: data.map((item) => item._id),
    };

    try {
      const response = await CollectionAPI.createNewCollection(collectionData);
      dispatch(setSuccessMessage(`Collection "${formData.collection_name}" created successfully`));
      navigate("/collections");
    } catch (error) {
      dispatch(setMessage(`${error.response?.data?.error || "Failed to create collection"}`, "danger"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let ids = searchParams.get('resources_id');
    async function getResourcesImages(ids) {
      const res = await axios.get(API_URL + `resources/getbulk/?ids=${ids}`);
      if (res.status === 200) {
        setData(res.data);
      }
    }
    if (ids) {
      getResourcesImages(ids);
    }
  }, []);

  return (
    <AuthLayout>
      <header className={"d-flex justify-content-between align-items-center mb-4"}>
        <h2 className="fs-5">{"Create Collection"}</h2>
        <Link to="/resources" className="btn btn-outline-secondary">
          Back to Resources
        </Link>
      </header>

      {message && (
        <div className="mb-4">
          <div className={`alert alert-${messageType}`} role="alert">
            {message}
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-md-8">
          <h5>Selected Resources ({data.length})</h5>
          <div className="row">
            {data.map((item, index) => (
              <div key={index} className="col-md-3 mb-3">
                <img
                  className="img-thumbnail"
                  src={`${MEDIA_URL}${item.coverImages.path}`}
                  width="200"
                  alt={item.name}
                />
                <p className="mt-2 mb-1 text-center small">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="col-md-4">
          <form onSubmit={handleSubmit(createCollection)} className={"p-4 border rounded"}>
            <div className="mb-3">
              <label htmlFor="collection_name" className="form-label">
                Collection Name *
              </label>
              <input
                id="collection_name"
                type="text"
                className="form-control"
                placeholder="Enter collection name"
                {...register("collection_name", { required: true })}
                onChange={(event) => {
                  generateCollectionTags(event);
                }}
              />
              {errors.collection_name && (
                <span className="small text-danger">This field is required</span>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="tags" className="form-label">
                Tags
              </label>
              <input
                id="collection_tags"
                className="form-control"
                defaultValue=""
                hidden
                placeholder="Enter tags"
                {...register("collection_tags")}
              />
              <TagsInput
                value={selectedTags}
                onChange={handleCollectionTags}
                onRemoved={handleRemoveCollectionTag}
                name="input-tags"
                placeHolder="Enter tags"
              />
              {suggestedTags.length > 0 && (
                <div className="mt-2 tags-lbox">
                  <small className="text-muted d-block mb-1">Suggested tags from collection name:</small>
                  {suggestedTags.map((tag, i) => (
                    <span
                      key={i}
                      className="badge bg-white border rounded me-2 text-dark fw-normal fs-6 my-1 py-2 px-3 suggested-tag"
                      style={{ cursor: 'pointer' }}
                      onClick={() => addSuggestedTag(tag)}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={isLoading || data.length === 0}
            >
              {isLoading ? "Creating..." : "Create Collection"}
            </button>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
}

function mapStateToProps(state) {
  const { token } = state.auth;
  const { message, messageType } = state.message;
  return {
    token,
    message,
    messageType,
  };
}

export default connect(mapStateToProps)(ResourcesCreateCollection);
